import * as React from "react";
import * as ReactGA from "react-ga";

import { HashRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SessionTracker } from "./components/SessionTracker";
import {
  ActiveFBOContainer,
  useActiveFBOs,
} from "./containers/ActiveFBOContainer";
import { AnnouncementContainer } from "./containers/AnnouncementContainer";
import { ApiContainer } from "./containers/ApiContainer";
import { DraggableAircraftContainer } from "./containers/DraggableAircraftContainer";
import { EventContainerProvider } from "./containers/EventContainer";
import {
  FeatureFlagsContainer,
  useFeatureFlags,
} from "./containers/FeatureFlagsContainer";
import { IdentityContainer, useIdentity } from "./containers/IdentityContainer";
import { LoadingProgressContainer } from "./containers/LoadingProgressContainer";
import { SchemaContainer } from "./containers/SchemaContainer";
import { SnackbarContainer } from "./containers/SnackbarContainer";
import { ThemeContainer } from "./containers/ThemeContainer";
import { UserPreferencesContainer } from "./containers/UserPreferencesContainer";
import { storeRedirectUrl } from "./utils/redirectManager";
import "./main.css";
import { Estimator } from "./screens/Estimator";
import { FlaggedStacks } from "./screens/FlaggedStacks";
import { ForgotPassword } from "./screens/ForgotPassword";
import { Hangar } from "./screens/Hangar";
import { HangarSchedule } from "./screens/Hangar/HangarSchedule";
import { HangarAlgorithmDev } from "./screens/HangarAlgorithmDev";
import { HangarBuilderOptimizer } from "./screens/HangarBuilderOptimizer/HangarBuilderOptimizer";
import { Hangars } from "./screens/Hangars";
import { LabelingTool } from "./screens/LabelingTool";
import { Login } from "./screens/Login";
import { LoginWithLink } from "./screens/LoginWithLink/LoginWithLink";
import { MagicLinkLogin } from "./screens/MagicLinkLogin/MagicLinkLogin";
import { Maintenance } from "./screens/Maintenance";
import { MultiHangar } from "./screens/MultiHangar";
import { NoActiveFBO } from "./screens/NoActiveFBO";
import { Ramp } from "./screens/Ramp";
import { RampSchedule } from "./screens/Ramp/RampSchedule";
import { TemporalTest } from "./screens/Ramp/TemporalTest";
import { RampOptimization } from "./screens/RampOptimization/RampOptimization";
import { Ramps } from "./screens/Ramps";
import { ResetPassword } from "./screens/ResetPassword";
import { ScheduleDashboard } from "./screens/Schedule/ScheduleDashboard";
import { RampEditorV2Wrapper } from "./screens/Settings/RampsSettings/RampEditorV2";
import { Signup } from "./screens/Signup";
import { StackMaster } from "./screens/StackMaster";
import { DevTools } from "./screens/DevTools";
import { AutoStackingLab } from "./screens/AutoStackingLab";
import { Ops } from "./screens/Ops";
import { QuarantineView } from "./screens/Quarantine";
import OperationsInboxView from "./screens/OperationsInbox/OperationsInboxView";
import { SuperAdmin } from "./screens/SuperAdmin";
import { Tenants } from "./screens/TransientsAndTenants";
import { VerifyEmail } from "./screens/VerifyEmail";
import { FlingLogsContainer } from "./utils/logging";
import { LoadingProgress } from "./widgets/LoadingProgress";
import { Nav } from "./widgets/Nav";
import { RoleTestingFab } from "./widgets/RoleTestingFab";
import { LanguageSwitcherFab } from "./widgets/LanguageSwitcherFab";
import { ActivationChecklistSwitcher } from "./components/Activation";
import { Snackbar } from "./widgets/Snackbar";

// Lazy load heavy screens to reduce initial bundle size
const Settings = React.lazy(() =>
  import("./screens/Settings").then((module) => ({ default: module.Settings }))
);

// Lazy load OpsCenter to defer Leaflet library (3.8 MB)
const OpsCenter = React.lazy(() =>
  import("./screens/OpsCenter").then((module) => ({ default: module.OpsCenter }))
);

const OpsCenterTV = React.lazy(() =>
  import("./screens/OpsCenter/OpsCenterTV").then((module) => ({ default: module.OpsCenterTV }))
);

// Lazy load ConvertSVG utility screen
const ConvertSVG = React.lazy(() =>
  import("./screens/ConvertSVG").then((module) => ({ default: module.ConvertSVG }))
);

const TRACKING_ID = "G-80LQDPGBHS";
ReactGA.initialize(TRACKING_ID);

type Props = {
  children?: React.ReactNode;
};

const ProtectedRoute: React.FC<{
  mustBelongtoFBO?: boolean;
  isAdmin?: boolean;
  requiresScheduling?: boolean;
}> = ({
  mustBelongtoFBO = false,
  isAdmin = false,
  requiresScheduling = false,
}) => {
  const { isLoggedIn, airplxIdentity } = useIdentity();
  const { ready, activeFBO } = useActiveFBOs();

  if (!isLoggedIn) {
    // Store where the user was trying to go before redirecting to login
    storeRedirectUrl(window.location.hash);
    return (
      <Navigate to={"/login"} replace state={{ from: window.location.hash }} />
    );
  }

  if (mustBelongtoFBO) {
    // async so need to wait until it's pulled data
    if (!ready) {
      return null;
    }
    if (!activeFBO) {
      return <NoActiveFBO />;
    }
  }

  if (isAdmin && !airplxIdentity?.isadmin) {
    return <Navigate to={"/"} replace />;
  }

  if (requiresScheduling && !activeFBO?.settings?.enableScheduling) {
    return <Navigate to={"/hangars"} replace />;
  }

  return <Outlet />;
};

export const MainContainer: React.FC<Props> = ({ children, ...props }) => {
  return (
    <FeatureFlagsContainer>
      <EventContainerProvider>
        <SnackbarContainer>
          <LoadingProgressContainer>
            <IdentityContainer {...props}>
              <ApiContainer>
                <FlingLogsContainer>
                  <SchemaContainer>
                    <UserPreferencesContainer>
                      <ThemeContainer>
                        <ActiveFBOContainer>
                          <DraggableAircraftContainer>
                            <AnnouncementContainer enabled={true}>
                              {children}
                            </AnnouncementContainer>
                          </DraggableAircraftContainer>
                        </ActiveFBOContainer>
                      </ThemeContainer>
                    </UserPreferencesContainer>
                  </SchemaContainer>
                </FlingLogsContainer>
              </ApiContainer>
            </IdentityContainer>
          </LoadingProgressContainer>
        </SnackbarContainer>
      </EventContainerProvider>
    </FeatureFlagsContainer>
  );
};

export const Main: React.FC<Props> = ({ ...props }) => {
  return (
    <ErrorBoundary>
      <MainContainer>
        <App {...props}></App>
      </MainContainer>
    </ErrorBoundary>
  );
};

export const App: React.FC<Props> = ({ }) => {
  if (Boolean(useFeatureFlags().isMaintenance)) {
    return <Maintenance />;
  }

  return (
    <SessionTracker>
      <HashRouter>
        <Nav>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-email/:code" element={<VerifyEmail />} />
            <Route path="/login/magic-link" element={<Login magicLink />} />
            <Route path="/login/link" element={<LoginWithLink />} />
            <Route path="/login/link/:code" element={<MagicLinkLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:code" element={<ResetPassword />} />
            {/* needs to be logged in, but doesn't need to be in an FBO yet */}
            <Route element={<ProtectedRoute />}>
              <Route path="/claim-invite/:inviteId" element={<NoActiveFBO />} />
            </Route>
            <Route element={<ProtectedRoute mustBelongtoFBO />}>
              <Route path="/" element={<Navigate to="/hangars" replace />} />
              <Route path="/hangars" element={<Hangars />} />
              <Route path="/hangars/:id" element={<Hangar key="current" />} />
              <Route path="/multi-hangar/:ids" element={<MultiHangar />} />
              <Route
                path="/reference-hangars/:id"
                element={<Hangar key="reference" isReference={true} />}
              />
              <Route
                path="/algorithm-evaluation/:id"
                element={<HangarAlgorithmDev isReference={false} />}
              />
              <Route path="/tenants" element={<Tenants />} />
              {/* real app */}
              <Route path="/ramps" element={<Ramps />} />
              <Route path="/ramps/:id" element={<Ramp key="current" />} />
              <Route
                path="/ramps/optimization"
                element={<RampOptimization />}
              />
              <Route
                path="/reference-ramps/:id"
                element={<Ramp key="reference" isReference={true} />}
              />
              <Route
                path="/ops-center"
                element={
                  <React.Suspense fallback={<LoadingProgress />}>
                    <OpsCenter />
                  </React.Suspense>
                }
              />
              <Route
                path="/ops-center/tv"
                element={
                  <React.Suspense fallback={<LoadingProgress />}>
                    <OpsCenterTV />
                  </React.Suspense>
                }
              />
              <Route path="/fee-calculator" element={<Estimator />} />
              <Route
                path="/hangar-builder-optimizer"
                element={<HangarBuilderOptimizer />}
              />
              <Route
                path="/settings"
                element={
                  <React.Suspense fallback={<LoadingProgress />}>
                    <Settings />
                  </React.Suspense>
                }
              />
              <Route
                path="/settings/ramp/:id/edit"
                element={<RampEditorV2Wrapper />}
              />
              <Route path="/flagged-stacks" element={<FlaggedStacks />} />
              <Route path="/stackmaster" element={<StackMaster />} />
              <Route path="/stackmaster/:id" element={<StackMaster />} />
              <Route path="/dev/temporal-parking" element={<TemporalTest />} />
              <Route path="/dev-tools" element={<DevTools />} />
              <Route path="/quarantine" element={<QuarantineView />} />
              <Route path="/inbox" element={<OperationsInboxView />} />
              <Route path="/ops" element={<Ops />} />
            </Route>
            {/* Scheduling-protected routes */}
            <Route
              element={<ProtectedRoute mustBelongtoFBO requiresScheduling />}
            >
              <Route
                path="/hangars/:id/schedule"
                element={<HangarSchedule />}
              />
              <Route path="/ramps/:id/schedule" element={<RampSchedule />} />
              <Route path="/schedule" element={<ScheduleDashboard />} />
            </Route>
            {/* internal-only pages */}
            <Route element={<ProtectedRoute isAdmin />}>
              <Route path="/dev/auto-stacking-lab" element={<AutoStackingLab />} />
              <Route path="/labeling-tool" element={<LabelingTool />} />
              <Route
                path="/labeling-tool/:aircraftId"
                element={<LabelingTool />}
              />
              <Route
                path="/convert-svg"
                element={
                  <React.Suspense fallback={<LoadingProgress />}>
                    <ConvertSVG />
                  </React.Suspense>
                }
              />
              <Route path="/super-admin" element={<SuperAdmin />} />
            </Route>
          </Routes>
          <Snackbar />
          <LoadingProgress />
          <RoleTestingFab />
          <LanguageSwitcherFab />
          <ActivationChecklistSwitcher />
        </Nav>
        {/* <SupportButton /> */}
        {/* <TawkIntegration /> */}
      </HashRouter>
    </SessionTracker>
  );
};
